import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BusinessContactInfo } from '../../types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  contact: BusinessContactInfo | null
  basePath: string
}

export function BusinessContactInfoSheet({ open, onOpenChange, businessId, contact, basePath }: Props) {
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    label: '', email: '', phone: ''
  })

  useEffect(() => {
    if (open) {
      if (contact) {
        setData({ label: contact.label || '', email: contact.email, phone: contact.phone || '' })
      } else { reset() }
      setApiError(null); clearErrors()
    }
  }, [open, contact])

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) { reset(); clearErrors() }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setApiError(null)
    const resourcePath = `${basePath}/businesses/${businessId}/contact_infos`
    if (contact?.id) {
      patch(`${resourcePath}/${contact.id}`, { onSuccess: () => handleClose(true), onError: (errors) => setApiError(Object.values(errors).join(', ')) })
    } else {
      post(resourcePath, { onSuccess: () => handleClose(true), onError: (errors) => setApiError(Object.values(errors).join(', ')) })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6"><SheetTitle>{contact ? 'Edit Contact Info' : 'Add Contact Info'}</SheetTitle></SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {apiError && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{apiError}</div>}
          <div className="space-y-2">
            <Label htmlFor="label">Label <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input id="label" placeholder="e.g. Primary, Billing" value={data.label} onChange={e => setData('label', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
            <Input id="email" type="email" placeholder="contact@example.com" value={data.email} onChange={e => setData('email', e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input id="phone" type="tel" placeholder="+1 555-0100" value={data.phone} onChange={e => setData('phone', e.target.value)} />
          </div>
          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={processing}>{processing ? 'Saving...' : contact ? 'Update' : 'Add Contact'}</Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
